import type { OrbVariant } from "@/types/ai-assistant-orb";

/** Teks bagian "Logo Jenna" (orb animasi) di /admin/ai-assistant. */
export interface AssistantOrbText {
	title: string;
	description: string;
	shape: string;
	size: string;
	sizeNames: Record<string, string>;
	variants: Record<OrbVariant, { name: string; hint: string }>;
	colors: string;
	colorFrom: string;
	colorTo: string;
	presets: string;
	presetNames: Record<string, string>;
	followTheme: string;
	followThemeHint: string;
	/** Penjelas saat varian berpalet tetap (pemilih warna dinonaktifkan). */
	fixedPalette: string;
	/** Penjelas saat varian tidak punya parameter yang bisa diatur. */
	noParams: string;
	settings: string;
	/** Label slider; kunci = `labelKey` pada ORB_VARIANT_SPECS. */
	params: Record<string, string>;
	preview: string;
	previewDark: string;
	previewLight: string;
	previewFab: string;
	previewHeader: string;
	save: string;
	saved: string;
	cancel: string;
	reset: string;
	resetDone: string;
	unavailable: string;
	loadFailed: string;
	saveFailed: string;
	reducedMotion: string;
}

const id: AssistantOrbText = {
	title: "Logo {name}",
	description:
		"Bentuk bercahaya yang dipakai di tombol bulat kanan bawah dan di samping nama {name} pada header panel. Digambar langsung di peramban, jadi tidak perlu mengunggah berkas.",
	shape: "Bentuk",
	size: "Ukuran",
	sizeNames: { kecil: "Kecil", sedang: "Sedang", besar: "Besar" },
	variants: {
		"particle-sphere": {
			name: "Partikel",
			hint: "Ribuan titik di permukaan bola yang berputar pelan.",
		},
		"wireframe-mesh": {
			name: "Jala",
			hint: "Bola dari garis jala yang permukaannya bergelombang.",
		},
		"glow-ring": {
			name: "Cincin",
			hint: "Cincin tipis bercahaya yang berdenyut. Paling ringan.",
		},
		"liquid-glass-flow": {
			name: "Kaca Cair — Mengalir",
			hint: "Permukaan kaca mengalir dengan warna dari gradien di atas. Render WebGL, jatuh ke Cincin bila tak didukung.",
		},
		"liquid-glass-dramatic": {
			name: "Kaca Cair — Dramatis",
			hint: "Seperti Mengalir, tapi distorsi & kilau lebih kuat. Render WebGL, jatuh ke Cincin bila tak didukung.",
		},
		"liquid-morph": {
			name: "Cairan Berubah Bentuk",
			hint: "Siluet orb berubah seperti cairan, dengan tepi berwarna prisma. Warna tetap. Render WebGL, jatuh ke Cincin bila tak didukung.",
		},
	},
	colors: "Warna",
	colorFrom: "Dari",
	colorTo: "Ke",
	presets: "Preset cepat",
	presetNames: {
		darmasaba: "Darmasaba",
		samudra: "Samudra",
		senja: "Senja",
		zamrud: "Zamrud",
		bara: "Bara",
		monokrom: "Monokrom",
	},
	followTheme: "Ikut tema dashboard",
	followThemeHint:
		"Warna di atas diabaikan; orb memakai warna utama dashboard dan ikut berubah bila palet diganti.",
	fixedPalette:
		'Warna varian ini tetap (spektrum prisma) dan tidak bisa diubah. Pilihan warna, preset, dan "Ikut tema" tidak berpengaruh di sini.',
	noParams: "Varian ini tidak punya parameter yang bisa diatur.",
	settings: "Pengaturan",
	params: {
		density: "Kerapatan titik",
		meshDensity: "Kerapatan jala",
		amplitude: "Amplitudo gelombang",
		speed: "Kecepatan",
		pulseSpeed: "Kecepatan denyut",
		glow: "Kekuatan cahaya",
		tilt: "Kemiringan",
		thickness: "Ketebalan cincin",
		core: "Inti",
		lgDistortion: "Distorsi permukaan",
		lgFlowScale: "Skala pola warna",
		lgSparkle: "Kilau",
	},
	preview: "Pratinjau",
	previewDark: "Gelap",
	previewLight: "Terang",
	previewFab: "Tombol bulat",
	previewHeader: "Header panel",
	save: "Simpan",
	saved: "Tampilan orb tersimpan",
	cancel: "Batal",
	reset: "Kembalikan ke bawaan",
	resetDone: "Tampilan orb dikembalikan ke bawaan",
	unavailable: "Pengaturan orb belum tersedia di server ini.",
	loadFailed: "Gagal memuat tampilan orb",
	saveFailed: "Gagal menyimpan tampilan orb",
	reducedMotion:
		"Gerak sedang dikurangi (setelan sistem atau animasi dashboard dimatikan), jadi orb ditampilkan diam.",
};

const en: AssistantOrbText = {
	title: "{name} logo",
	description:
		"The glowing shape used on the round button at the bottom right and next to {name}'s name in the panel header. It is drawn in the browser, so there is no file to upload.",
	shape: "Shape",
	size: "Size",
	sizeNames: { kecil: "Small", sedang: "Medium", besar: "Large" },
	variants: {
		"particle-sphere": {
			name: "Particles",
			hint: "Thousands of dots on a slowly rotating sphere.",
		},
		"wireframe-mesh": {
			name: "Mesh",
			hint: "A sphere of mesh lines with a rippling surface.",
		},
		"glow-ring": {
			name: "Ring",
			hint: "A thin glowing ring that pulses. The lightest option.",
		},
		"liquid-glass-flow": {
			name: "Liquid Glass — Flow",
			hint: "A flowing glass surface coloured from the gradient above. WebGL; falls back to Ring where unsupported.",
		},
		"liquid-glass-dramatic": {
			name: "Liquid Glass — Dramatic",
			hint: "Like Flow, with stronger distortion and glint. WebGL; falls back to Ring where unsupported.",
		},
		"liquid-morph": {
			name: "Liquid Morph",
			hint: "The orb's silhouette morphs like liquid, with prism-coloured edges. Fixed colours. WebGL; falls back to Ring where unsupported.",
		},
	},
	colors: "Colours",
	colorFrom: "From",
	colorTo: "To",
	presets: "Quick presets",
	presetNames: {
		darmasaba: "Darmasaba",
		samudra: "Ocean",
		senja: "Dusk",
		zamrud: "Emerald",
		bara: "Ember",
		monokrom: "Monochrome",
	},
	followTheme: "Follow the dashboard theme",
	followThemeHint:
		"The colours above are ignored; the orb uses the dashboard's primary colours and follows any palette change.",
	fixedPalette:
		'This variant\'s colours are fixed (prism spectrum) and cannot be changed. The colour pickers, presets and "Follow the dashboard theme" have no effect here.',
	noParams: "This variant has no adjustable parameters.",
	settings: "Settings",
	params: {
		density: "Dot density",
		meshDensity: "Mesh density",
		amplitude: "Ripple amplitude",
		speed: "Speed",
		pulseSpeed: "Pulse speed",
		glow: "Glow strength",
		tilt: "Tilt",
		thickness: "Ring thickness",
		core: "Core",
		lgDistortion: "Surface distortion",
		lgFlowScale: "Colour flow scale",
		lgSparkle: "Glint",
	},
	preview: "Preview",
	previewDark: "Dark",
	previewLight: "Light",
	previewFab: "Round button",
	previewHeader: "Panel header",
	save: "Save",
	saved: "Orb look saved",
	cancel: "Cancel",
	reset: "Restore defaults",
	resetDone: "Orb look restored to defaults",
	unavailable: "Orb settings are not available on this server.",
	loadFailed: "Failed to load the orb look",
	saveFailed: "Failed to save the orb look",
	reducedMotion:
		"Motion is reduced (system setting or dashboard animations are off), so the orb is shown still.",
};

export const assistantOrbTexts = { id, en } as const;
